import {
  BankingCompany,
  binaryValueFormatter,
  DO_APPLICATION_TYPE,
  useSectionFormHelpers,
} from 'client-experience-toolbox';
import { FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup } from 'drift-ui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { useUpdateApplication } from 'utils/useUpdateApplication';
import { sendDOFirebaseEvent } from 'utils/utils';
import baseStyles from './BaseFormStyles.scss';
import styles from './BusinessBackground.scss';
import cx from 'classnames';
import ContinueButton from 'components/ContinueButton';
import { useApplication } from 'hooks/useApplication';

type BankingApplicationDataType = {
  company?: Partial<BankingCompany>;
};

export const BusinessBackground = () => {
  const { application } = useApplication(DO_APPLICATION_TYPE.BANKING);
  const applicationData = application?.applicationData as BankingApplicationDataType;
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.BANKING);
  const [isPatching, setIsPatching] = useState(false);
  const { patchApplication, patchFullApplication } = useFormNetworkCalls(DO_APPLICATION_TYPE.BANKING);
  const { binaryHandlerGenerator, clearValidation, presenceValidatorGenerator, handleInputFocus } =
    useSectionFormHelpers({
      errorSetter: setErrors,
      patchApplication,
      application,
      updateApplication,
      fireBaseLogEvent: sendDOFirebaseEvent,
    });

  const { t } = useTranslation();

  const fieldValidators: { [key: string]: () => boolean } = {
    operatesInUSD: presenceValidatorGenerator({
      key: 'applicationData.company.operatesInUSD',
      errorText: t('Required'),
    }),
    operatesInUS: presenceValidatorGenerator({
      key: 'applicationData.company.operatesInUS',
      errorText: t('Required'),
    }),
  };

  const handleContinueClick = async () => {
    const results = Object.values(fieldValidators).map((validator, index) => ({
      index,
      isValid: validator(),
    }));
    const firstInvalid = results.find(({ isValid }) => !isValid);

    if (firstInvalid) {
      return;
    }

    if (!applicationData?.company?.operatesInUS || !applicationData?.company?.operatesInUSD) {
      patchApplication('lockedBy', 'businessBackground');
      updateApplication('lockedBy', 'businessBackground');
    } else {
      await patchFullApplication(setIsPatching);
    }
  };

  return (
    <div className={cx(baseStyles.formContainer, styles.questionContainer)}>
      <div className={styles.questionFormContainer}>
        <div>
          <p
            className={styles.questionTitle}
            style={{ color: errors[`applicationData.company.operatesInUSD`] ? 'red' : '#444' }}
          >
            {t('digital_onboarding.banking.business_background.operate_usd_question')}
          </p>
          <FormControl error={!!errors[`applicationData.company.operatesInUSD`]}>
            <RadioGroup
              data-nid-target="business-background-operates-with-usd"
              value={binaryValueFormatter(applicationData?.company?.operatesInUSD)}
              onChange={binaryHandlerGenerator('applicationData.company.operatesInUSD', clearValidation)}
              onFocus={handleInputFocus}
            >
              <FormControlLabel
                value="yes"
                control={
                  <Radio
                    slotProps={{
                      input: {
                        'data-nid-target': 'business-background-operates-with-usd-yes',
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={t('yes')}
              />
              <FormControlLabel
                value="no"
                control={
                  <Radio
                    slotProps={{
                      input: {
                        'data-nid-target': 'business-background-operates-with-usd-no',
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={t('no')}
              />
            </RadioGroup>
            {errors[`applicationData.company.operatesInUSD`] && (
              <FormHelperText>{errors[`applicationData.company.operatesInUSD`]}</FormHelperText>
            )}
          </FormControl>
        </div>
        <div>
          <p
            className={styles.questionTitle}
            style={{ color: errors[`applicationData.company.operatesInUS`] ? 'red' : '#444' }}
          >
            {t('digital_onboarding.banking.business_background.company_operate_question')}
          </p>
          <FormControl error={!!errors['applicationData.company.operatesInUS']}>
            <RadioGroup
              data-nid-target="business-background-operates-in-usa"
              value={binaryValueFormatter(applicationData?.company?.operatesInUS)}
              onChange={binaryHandlerGenerator('applicationData.company.operatesInUS', clearValidation)}
              onFocus={handleInputFocus}
            >
              <FormControlLabel
                value="yes"
                control={
                  <Radio
                    slotProps={{
                      input: {
                        'data-nid-target': 'business-background-operates-in-usa-yes',
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={t('USA')}
              />
              <FormControlLabel
                value="no"
                control={
                  <Radio
                    slotProps={{
                      input: {
                        'data-nid-target': 'business-background-operates-in-usa-no',
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={t('Somewhere else')}
              />
            </RadioGroup>
            {errors[`applicationData.company.operatesInUS`] && (
              <FormHelperText>{errors[`applicationData.company.operatesInUS`]}</FormHelperText>
            )}
          </FormControl>
        </div>
      </div>
      <div className={styles.footer}>
        <ContinueButton
          buttonStyles={baseStyles.buttonStyle}
          handleContinueClick={handleContinueClick}
          isPatching={isPatching}
          title={t('digital_onboarding.next')}
        />
      </div>
    </div>
  );
};
